import { useUTLSettingsContext } from "@dexteel/mesf-core";
import {
	CellClickedEvent,
	CellValueChangedEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { get } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
	deleteChemistrySample,
	getChemistrySampleAttributes,
	upsertChemistryResult,
	upsertChemistrySample,
} from "../../repositories/ChemistrySampleRepository";

export interface SampleAttributesForm {
	SampleTypeId: number | null;
	SampleTypeName: string;
	Reported: Date | null;
	SampleSequence: number | null;
	Technician: string;
	Comments: string;
}

interface SelectedSample {
	ChemistrySampleId: number | null;
	SampleTypeName: string | null;
	SampleSequence: number | null;
	ResultSourceId: number | null;
}

interface UseSampleEditorProps {
	dataSource: any[];
	heatId: number | null;
	gradeId: number | null;
	onDataRefresh: () => Promise<void>;
}

const DEFAULT_FORM_VALUES: SampleAttributesForm = {
	SampleTypeId: null,
	SampleTypeName: "",
	Reported: null,
	SampleSequence: null,
	Technician: "",
	Comments: "",
};

export const useSampleEditor = ({
	dataSource,
	heatId,
	gradeId,
	onDataRefresh,
}: UseSampleEditorProps) => {
	const { state: utlSettingsState } = useUTLSettingsContext();
	const [selectedSample, setSelectedSample] = useState<SelectedSample | null>(
		null,
	);
	const [originalReportedDate, setOriginalReportedDate] = useState<Date | null>(
		null,
	);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoadingSample, setIsLoadingSample] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
	const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

	// Get max age days setting (default 30)
	const MAX_AGE_DAYS = useMemo(() => {
		const settingValue = get(
			utlSettingsState.settings,
			"LAB_DATA_ENTRY_SAMPLE_MAX_EDITABLE_AGE_TIME_DAYS",
			"30",
		);
		return Number(settingValue) || 30;
	}, [utlSettingsState.settings]);

	// Check if a sample is too old to edit
	const isSampleTooOld = useCallback(
		(reportedDate: Date | null): boolean => {
			if (!reportedDate) return false;
			const daysDiff =
				(Date.now() - new Date(reportedDate).getTime()) / (1000 * 60 * 60 * 24);
			return daysDiff > MAX_AGE_DAYS;
		},
		[MAX_AGE_DAYS],
	);

	const form = useForm<SampleAttributesForm>({
		defaultValues: DEFAULT_FORM_VALUES,
	});
	const {
		reset,
		setValue,
		handleSubmit,
		control,
		watch,
		formState: { isDirty },
	} = form;

	// Check if current sample is too old based on ORIGINAL date (not form value)
	const currentSampleIsTooOld = useMemo(
		() => (originalReportedDate ? isSampleTooOld(originalReportedDate) : false),
		[originalReportedDate, isSampleTooOld],
	);

	// Helper to wrap actions that should confirm if there are unsaved changes
	const confirmIfDirty = useCallback(
		(action: () => void) => {
			if (isDirty) {
				setPendingAction(() => action);
				setShowUnsavedConfirm(true);
			} else {
				action();
			}
		},
		[isDirty],
	);

	const handleUnsavedConfirmClose = useCallback(
		(value?: string) => {
			if (value === "OK" && pendingAction) {
				pendingAction();
			}
			setPendingAction(null);
			setShowUnsavedConfirm(false);
		},
		[pendingAction],
	);

	// Helpers
	const clearSelectedSample = useCallback(() => {
		setSelectedSample(null);
		setOriginalReportedDate(null);
		reset(DEFAULT_FORM_VALUES);
	}, [reset]);

	const withSaving = async <T>(fn: () => Promise<T>): Promise<T | null> => {
		setIsSaving(true);
		setErrorMessage("");
		try {
			return await fn();
		} catch (error: any) {
			setErrorMessage(error.message || "An error occurred.");
			return null;
		} finally {
			setIsSaving(false);
		}
	};

	const setSampleFromData = useCallback(
		(sampleData: any, data: any, comments = "") => {
			const reportedDate = data.DateResults
				? new Date(data.DateResults)
				: data.Reception || null;
			// Store the ORIGINAL reported date for validation
			setOriginalReportedDate(reportedDate);
			setSelectedSample({
				ChemistrySampleId: sampleData.ChemistrySampleId,
				SampleTypeName: sampleData.SampleTypeName,
				SampleSequence: sampleData.SampleSequence,
				ResultSourceId: data.ResultSourceId || null,
			});
			reset({
				SampleTypeId: data.SampleTypeId,
				SampleTypeName: data.SampleTypeName || data.SampleName || "",
				Reported: reportedDate,
				SampleSequence: data.SampleSequence,
				Technician: data.Technician || "",
				Comments: comments,
			});
		},
		[reset],
	);

	// Cell editing handlers
	const handleCellEditingStarted = useCallback(() => setIsEditing(true), []);
	const handleCellEditingStopped = useCallback(() => setIsEditing(false), []);

	// Load sample data for a given row
	const loadSampleForRow = useCallback(
		async (rowData: any) => {
			const sampleId = rowData.id;
			const sampleData = dataSource.find(
				(s: any) =>
					`${s.SampleTypeName || "?"}${s.SampleSequence ?? ""}` === sampleId,
			);

			if (!sampleData?.ChemistrySampleId) {
				clearSelectedSample();
				return;
			}

			if (!heatId) {
				setSelectedSample({
					ChemistrySampleId: sampleData.ChemistrySampleId,
					SampleTypeName: sampleData.SampleTypeName,
					SampleSequence: sampleData.SampleSequence,
					ResultSourceId: sampleData.ResultSourceId || null,
				});
				return;
			}

			setIsLoadingSample(true);
			try {
				const resp = await getChemistrySampleAttributes(
					sampleData.ChemistrySampleId,
					heatId,
				);
				if (resp.ok && resp.data?.tables[0]?.rows[0]) {
					const sample = resp.data.tables[0].rows[0];
					setSampleFromData(sampleData, sample, sample.Comments || "");
				} else {
					const fallback = dataSource.find(
						(s: any) => s.ChemistrySampleId === sampleData.ChemistrySampleId,
					);
					if (fallback) setSampleFromData(sampleData, fallback);
				}
			} catch {
				const fallback = dataSource.find(
					(s: any) => s.ChemistrySampleId === sampleData.ChemistrySampleId,
				);
				if (fallback) setSampleFromData(sampleData, fallback);
			} finally {
				setIsLoadingSample(false);
			}
		},
		[dataSource, heatId, clearSelectedSample, setSampleFromData],
	);

	// Handle cell click to load sample data (works for all columns including Sample/Time)
	const handleCellClicked = useCallback(
		(params: CellClickedEvent) => {
			// Skip MIN/MAX rows
			if (
				!params.data ||
				params.data.id === "MIN" ||
				params.data.id === "MAX"
			) {
				return;
			}
			// Load sample data for the clicked row, with confirmation if dirty
			// Selection is handled manually after confirmation to prevent premature selection change
			confirmIfDirty(() => {
				loadSampleForRow(params.data);
				// Manually select the row after loading
				params.node.setSelected(true, true);
			});
		},
		[loadSampleForRow, confirmIfDirty],
	);

	const handleCellValueChanged = useCallback(
		async (params: CellValueChangedEvent) => {
			if (!params.data || !params.colDef?.field) return;
			if (params.data.id === "MIN" || params.data.id === "MAX") return;

			const elementName = params.colDef.field;
			const colData = params.data[elementName];

			const chemistrySampleId =
				(colData && typeof colData === "object"
					? colData.chemistrySampleId
					: null) || params.data.ChemistrySampleId;
			const elementId =
				colData && typeof colData === "object" ? colData.elementId : null;

			if (!chemistrySampleId || !elementId) {
				setErrorMessage("Cannot save: Missing sample or element information.");
				return;
			}

			// Check if sample is too old to edit (using Reception date from row data)
			if (params.data.Reception && isSampleTooOld(params.data.Reception)) {
				setErrorMessage(
					`Cannot save: This sample is older than ${MAX_AGE_DAYS} days. Chemistry results for samples older than ${MAX_AGE_DAYS} days cannot be modified.`,
				);
				return;
			}

			const result = await withSaving(async () => {
				return await upsertChemistryResult({
					Id: null,
					ChemistrySampleId: chemistrySampleId,
					ElementId: elementId,
					Value: params.newValue == null ? null : Number(params.newValue),
				});
			});

			if (result?.ok) {
				setNotificationMessage("Chemistry result saved successfully.");
				await onDataRefresh();
			} else if (result) {
				setErrorMessage(result.message || "Failed to save chemistry result.");
			}
		},
		[onDataRefresh, isSampleTooOld, MAX_AGE_DAYS],
	);

	// Selection handler - only handles deselection, cell click handles loading
	const handleSelectionChanged = useCallback(
		async (params: SelectionChangedEvent) => {
			const selectedNodes = params.api.getSelectedNodes();

			if (!selectedNodes.length) {
				clearSelectedSample();
				return;
			}

			const selectedData = selectedNodes[0].data;
			if (
				!selectedData ||
				selectedData.id === "MIN" ||
				selectedData.id === "MAX"
			) {
				clearSelectedSample();
				return;
			}
			// Note: loadSampleForRow is called by handleCellClicked, not here
			// to avoid duplicate API calls
		},
		[clearSelectedSample],
	);

	// Form handlers
	const handleSampleAttributesSubmit = useCallback(
		async (data: SampleAttributesForm) => {
			if (!selectedSample?.ChemistrySampleId || !heatId) {
				setErrorMessage("Cannot save: Missing sample or heat information.");
				return;
			}
			if (!selectedSample.ResultSourceId) {
				setErrorMessage("Cannot save: Missing Result Source information.");
				return;
			}
			// For existing samples: validate against ORIGINAL date
			if (originalReportedDate) {
				if (isSampleTooOld(originalReportedDate)) {
					setErrorMessage(
						`Cannot save: Sample is older than ${MAX_AGE_DAYS} days. Samples older than ${MAX_AGE_DAYS} days cannot be modified.`,
					);
					return;
				}
				// Prevent changing date to be older than original
				if (
					data.Reported &&
					originalReportedDate &&
					data.Reported < originalReportedDate
				) {
					setErrorMessage(
						`Cannot save: Cannot change the reported date to be older than the original date.`,
					);
					return;
				}
			}
			// For new samples: validate against form date
			if (!originalReportedDate && isSampleTooOld(data.Reported)) {
				setErrorMessage(
					`Cannot save: Sample date is older than ${MAX_AGE_DAYS} days. Samples older than ${MAX_AGE_DAYS} days cannot be created.`,
				);
				return;
			}

			const result = await withSaving(async () => {
				return await upsertChemistrySample({
					Id: selectedSample.ChemistrySampleId,
					SampleTypeId: data.SampleTypeId!,
					SampleSequence: data.SampleSequence,
					DateTimeResults: data.Reported,
					StatusCode: "V",
					ResultSourceId: selectedSample.ResultSourceId,
					Comments: data.Comments || "",
					HeatId: heatId,
					Technician: data.Technician || "",
					Melter: "",
					DateTimeReceived: null,
					DateResults: null,
					SampleName: data.SampleTypeName || "",
					DateError: false,
					DateString: "",
					ElementsId: null,
					ElementsValue: null,
					ResultsId: null,
					elementParams: [],
					sampleParams: [],
					isNew: null,
				});
			});

			if (result?.ok) {
				setNotificationMessage("Sample attributes saved successfully.");
				// Update original date if Reported date was changed
				if (data.Reported) {
					setOriginalReportedDate(data.Reported);
				}
				await onDataRefresh();
			} else if (result) {
				setErrorMessage(result.message || "Failed to save sample attributes.");
			}
		},
		[
			selectedSample,
			heatId,
			onDataRefresh,
			originalReportedDate,
			isSampleTooOld,
			MAX_AGE_DAYS,
		],
	);

	const handleSampleSelection = useCallback(
		(value: number | null, sampleTypeName: string) => {
			setValue("SampleTypeId", value, { shouldDirty: true });
			setValue("SampleTypeName", sampleTypeName, { shouldDirty: true });

			const existing = dataSource.find(
				(s: any) => s.SampleTypeName === sampleTypeName,
			);
			setValue(
				"SampleSequence",
				existing ? (existing.SampleSequence || 0) + 1 : 1,
				{ shouldDirty: true },
			);
		},
		[setValue, dataSource],
	);

	const handleFieldChange = useCallback(
		(
			field: keyof SampleAttributesForm,
			value: Date | string | number | null,
		) => {
			setValue(field, value as any, { shouldDirty: true });
		},
		[setValue],
	);

	const handleCancel = useCallback(
		() => clearSelectedSample(),
		[clearSelectedSample],
	);

	// Delete handlers
	const handleAddSample = useCallback(() => {
		if (!gradeId) {
			setErrorMessage(
				"No grade assigned. A grade (Cast Grade or Programmed Grade) must be assigned first.",
			);
			return false;
		}
		return true;
	}, [gradeId]);

	const handleDeleteSample = useCallback(
		async (value?: string) => {
			setShowDeleteConfirm(false);
			if (value !== "OK" || !selectedSample?.ChemistrySampleId) return;

			const result = await withSaving(() =>
				deleteChemistrySample(selectedSample.ChemistrySampleId!),
			);

			if (result?.ok) {
				clearSelectedSample();
				setNotificationMessage("Sample deleted successfully.");
				await onDataRefresh();
			} else if (result) {
				setErrorMessage(result.message || "Failed to delete sample.");
			}
		},
		[selectedSample, onDataRefresh, clearSelectedSample],
	);

	const handleDeleteSampleFromRow = useCallback(
		(chemistrySampleId: number) => {
			const sample = dataSource.find(
				(s: any) => s.ChemistrySampleId === chemistrySampleId,
			);
			if (sample) {
				setSelectedSample({
					ChemistrySampleId: chemistrySampleId,
					SampleTypeName: sample.SampleTypeName || null,
					SampleSequence: sample.SampleSequence || null,
					ResultSourceId: sample.ResultSourceId || null,
				});
				setShowDeleteConfirm(true);
			}
		},
		[dataSource],
	);

	return {
		// State
		selectedSample,
		isSaving,
		isLoadingSample,
		isEditing,
		isDirty,
		errorMessage,
		notificationMessage,
		showDeleteConfirm,
		showUnsavedConfirm,
		currentSampleIsTooOld,
		MAX_AGE_DAYS,

		// Form
		control,
		handleSubmit,

		// Setters
		setErrorMessage,
		setNotificationMessage,
		setShowDeleteConfirm,

		// Handlers for ElementTable
		handleCellValueChanged,
		handleCellEditingStarted,
		handleCellEditingStopped,
		handleSelectionChanged,
		handleCellClicked,

		// Handlers for form
		handleSampleAttributesSubmit,
		handleSampleSelection,
		handleFieldChange,
		handleCancel,
		handleAddSample,
		handleDeleteSample,
		handleDeleteSampleFromRow,

		// Handlers for unsaved changes confirmation
		confirmIfDirty,
		handleUnsavedConfirmClose,

		// Date validation
		isSampleTooOld,
	};
};
